import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { useRealtimeSync } from '../hooks/useRealtimeSync';

interface RealtimeContextValue {
  isConnected: boolean;
  pregnancyId: string | null;
  setPregnancyId: (id: string | null) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

interface RealtimeProviderProps {
  children: ReactNode;
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pregnancyId, setPregnancyId] = useState<string | null>(null);

  // Invalidate partner queries when partner connection changes
  const handlePartnerConnected = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['partner'] });
    queryClient.invalidateQueries({ queryKey: ['partner-connection'] });
  }, [queryClient]);

  const handlePartnerDisconnected = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['partner'] });
    queryClient.invalidateQueries({ queryKey: ['partner-connection'] });
  }, [queryClient]);

  const handlePartnerUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['partner'] });
  }, [queryClient]);

  const { isSubscribed } = useRealtimeSync({
    pregnancyId,
    userId: user?.id || null,
    onPartnerConnected: handlePartnerConnected,
    onPartnerDisconnected: handlePartnerDisconnected,
    onPartnerUpdated: handlePartnerUpdated,
  });

  // Store pregnancy ID when available
  useEffect(() => {
    // TODO: Get pregnancy ID from pregnancy store when available
    // For now, this will be set manually or via props
  }, [user]);

  const value: RealtimeContextValue = {
    isConnected: isSubscribed,
    pregnancyId,
    setPregnancyId,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
}
